import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Bot, Loader2, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useContactInfo } from "@/context/SiteContentContext";
import { API } from "@/lib/api";

const genId = () => "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);

// Lightweight markdown: **bold** + preserve newlines (whitespace-pre-wrap handles \n)
function renderMarkdown(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="font-bold text-primary">{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}

export default function Chatbot() {
  const { t, lang } = useLang();
  const CONTACT_INFO = useContactInfo();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(genId());
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t.chat.welcome }]);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, lang }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: ?/, "");
          if (!line) continue;
          let payload;
          try { payload = JSON.parse(line); } catch { continue; }
          if (payload.done) continue;
          if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1].content += " ⚠️";
              return copy;
            });
            continue;
          }
          if (payload.t) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.t };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t.contact.error };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.2, type: "spring" }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 end-6 z-[60] h-16 w-16 rounded-full grid place-items-center shadow-2xl"
        style={{ background: "var(--primary)", boxShadow: "0 0 30px rgba(0,242,96,0.5)" }}
        data-testid="chatbot-launcher"
        aria-label={t.chat.open}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={26} className="text-[#04120a]" />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <MessageCircle size={26} className="text-[#04120a]" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-28 end-6 z-[60] w-[calc(100vw-3rem)] max-w-sm h-[520px] max-h-[70vh] flex flex-col rounded-3xl overflow-hidden glass-strong border border-white/10 shadow-2xl"
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <div className="h-10 w-10 rounded-full grid place-items-center" style={{ background: "rgba(0,242,96,0.15)" }}>
                <Bot size={20} className="text-primary" />
              </div>
              <div>
                <div className="font-bold text-sm">{t.chat.title}</div>
                <div className="text-xs text-primary flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--primary)] animate-pulse" /> Online
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="ms-auto h-8 w-8 grid place-items-center rounded-full hover:bg-white/10 transition-colors" data-testid="chat-close" aria-label="close">
                <X size={18} className="text-slate-300" />
              </button>
            </div>

            {/* Talk to human — hands off latest user message as context */}
            {(() => {
              const lastUser = [...messages].reverse().find((m) => m.role === "user");
              const preface = t.chat.handoffPreface || "";
              const text = lastUser?.content ? `${preface}\n\n"${lastUser.content}"` : preface;
              const waHref = `https://wa.me/${CONTACT_INFO.whatsapp}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
              return (
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-2 text-xs font-bold border-b border-white/10 text-[#25D366] hover:bg-white/5 transition-colors"
                  data-testid="chat-human"
                >
                  <Phone size={14} /> {t.chatHuman}
                </a>
              );
            })()}

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "text-[#04120a] font-medium" : "bg-[#0d1836] text-slate-100 border border-white/5"}`}
                    style={m.role === "user" ? { background: "var(--primary)" } : {}}
                  >
                    {m.content ? renderMarkdown(m.content) : (streaming && i === messages.length - 1 ? <Loader2 size={16} className="animate-spin" /> : "")}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-white/10 flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder={t.chat.placeholder}
                className="flex-1 resize-none rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[color:var(--primary)] max-h-24"
                data-testid="chat-input"
              />
              <button
                onClick={send}
                disabled={streaming || !input.trim()}
                className="h-11 w-11 shrink-0 rounded-xl grid place-items-center text-[#04120a] disabled:opacity-40 transition-transform hover:scale-105"
                style={{ background: "var(--primary)" }}
                data-testid="chat-send"
              >
                {streaming ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
